// signatureRasterizer — type-your-signature with a user-provided font, then
// rasterize to a transparent PNG. The font is loaded into document.fonts only
// long enough to draw, then removed. The font file is never uploaded.

const FONT_MAX_BYTES = 1 * 1024 * 1024; // 1 MB
const FONT_ALLOWED_EXTS = [".woff2", ".woff", ".otf", ".ttf"] as const;


const randomFamilyName = (): string => {
  const rand = (typeof crypto !== "undefined" && "randomUUID" in crypto)
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `excerptus-sig-${rand}`;
};

export type LoadedSignatureFont = {
  family: string;
  fontFace: FontFace;
  cleanup: () => void;
};

export const validateSignatureFontFile = (file: File): string | null => {
  if (file.size > FONT_MAX_BYTES) {
    return `Font is too large (max ${Math.round(FONT_MAX_BYTES / 1024)} KB).`;
  }
  const lowered = file.name.toLowerCase();
  const okExt = FONT_ALLOWED_EXTS.some((e) => lowered.endsWith(e));
  if (!okExt) return "Use a .woff2, .woff, .otf, or .ttf font file.";
  return null;
};

/**
 * Load a font file into document.fonts under a unique throwaway family name.
 * Caller MUST invoke `cleanup()` once done (e.g. on unmount or after rasterize).
 */
export const loadSignatureFont = async (file: File): Promise<LoadedSignatureFont> => {
  const err = validateSignatureFontFile(file);
  if (err) throw new Error(err);
  const buf = await file.arrayBuffer();
  const family = randomFamilyName();
  const fontFace = new FontFace(family, buf);
  await fontFace.load();
  (document as Document & { fonts: FontFaceSet }).fonts.add(fontFace);
  const cleanup = () => {
    try {
      (document as Document & { fonts: FontFaceSet }).fonts.delete(fontFace);
    } catch {
      // non-fatal
    }
  };
  return { family, fontFace, cleanup };
};

export type RasterizeOptions = {
  text: string;
  family: string;
  fontPx: number;
  color: string;
  /** PNG render scale (3 = 3× pixel density for retina). */
  scale?: number;
  /** Padding around the text in CSS pixels (pre-scale). Used both for the
   *  initial draw buffer and for the final crop. */
  padding?: number;
};

/** Scan alpha channel and return the bounding box of non-transparent pixels. */
const findInkBounds = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
): { x: number; y: number; w: number; h: number } | null => {
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const a = data[(y * width + x) * 4 + 3];
      if (a > 4) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
};

/**
 * Render text in `family` to a transparent PNG. Trims to text bounds + padding.
 * Returns a `File` ready for upload via `uploadBlockImage`.
 */
export const rasterizeSignatureToPng = async (opts: RasterizeOptions): Promise<File> => {
  const { text, family, fontPx, color } = opts;
  const scale = opts.scale ?? 3;
  const padding = Math.max(0, opts.padding ?? 6);
  if (!text.trim()) throw new Error("Type your signature first.");

  // Generous initial draw buffer so any swashes/descenders fit. We'll crop
  // to actual ink afterwards, so over-allocating here is harmless.
  const fontStr = `${fontPx}px "${family}", cursive`;
  const measure = document.createElement("canvas");
  const mctx = measure.getContext("2d");
  if (!mctx) throw new Error("Canvas not supported.");
  mctx.font = fontStr;
  const metrics = mctx.measureText(text);
  const textWidth = Math.ceil(metrics.width);

  const bufferPad = Math.ceil(fontPx * 1.2); // headroom for tall glyphs
  const cssWidth = textWidth + bufferPad * 2;
  const cssHeight = Math.ceil(fontPx * 2.4) + bufferPad;
  const baselineY = Math.round(cssHeight * 0.6);

  const draw = document.createElement("canvas");
  draw.width = Math.max(1, Math.round(cssWidth * scale));
  draw.height = Math.max(1, Math.round(cssHeight * scale));
  const dctx = draw.getContext("2d");
  if (!dctx) throw new Error("Canvas not supported.");
  dctx.scale(scale, scale);
  dctx.clearRect(0, 0, cssWidth, cssHeight);
  dctx.fillStyle = color;
  dctx.font = fontStr;
  dctx.textBaseline = "alphabetic";
  dctx.fillText(text, bufferPad, baselineY);

  // Crop to actual ink bounds, then add user-controlled padding.
  const imgData = dctx.getImageData(0, 0, draw.width, draw.height);
  const bounds = findInkBounds(imgData.data, draw.width, draw.height);
  if (!bounds) throw new Error("No visible signature was rendered.");

  const padPx = Math.round(padding * scale);
  const outW = bounds.w + padPx * 2;
  const outH = bounds.h + padPx * 2;

  const out = document.createElement("canvas");
  out.width = outW;
  out.height = outH;
  const octx = out.getContext("2d");
  if (!octx) throw new Error("Canvas not supported.");
  octx.clearRect(0, 0, outW, outH);
  octx.drawImage(draw, bounds.x, bounds.y, bounds.w, bounds.h, padPx, padPx, bounds.w, bounds.h);

  const blob = await new Promise<Blob | null>((resolve) =>
    out.toBlob((b) => resolve(b), "image/png"),
  );
  if (!blob) throw new Error("Could not render signature to PNG.");
  return new File([blob], `signature-${Date.now()}.png`, { type: "image/png" });
};
